/**
 * Topo do painel de cada canal (/canal/<canal>): projeção do dia (ritmo vs. referência), mês × meta, saúde da
 * integração (conexão, autorização, erros 24 h) e da conta no canal (reputação, desempenho, loja), pedidos em risco
 * de atraso, pendências de cadastro por motivo (cada uma leva à lista filtrada) e preço × PMC oficial (acima e a
 * menos de 5%). Métrica que o canal não expõe aparece como "indisponível", nunca zero.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { refText } from './General.tsx';
import { pickAccount, useSelectedAccountId } from '../components/AccountPicker.tsx';
import { useConnectedChannels } from '../components/Shell.tsx';

type Level = 'ok' | 'attention' | 'critical' | 'unavailable';
interface HealthItem { metric: string; label: string; value: string | null; level: Level; detail: string | null }
export interface ChannelData {
  now: { today: string; time: string };
  projection: { amount: number | null; pacePct: number | null; method: string; refDays: string[]; average: { business: number | null; weekend: number | null }; soldToday: number; ordersToday: number };
  month: { amount: number; goal: number | null; goalPct: number | null; source: string | null; provisional: boolean };
  health: { accountId: string; name: string; level: Exclude<Level, 'unavailable'>; status: string; integration: HealthItem[]; account: HealthItem[] }[];
  risk: { count: number; late: number };
  pmc: { above: number; near: number };
  catalog: { published: number; ready: number; pending: number; reasons: { code: string; reason: string; n: number }[]; failed: number; failedTop: { error: string; n: number }[] };
}
const TONE: Record<Level, string> = { ok: 'ok', attention: 'warn', critical: 'err', unavailable: 'off' };
const LABEL: Record<Level, string> = { ok: 'ok', attention: 'atenção', critical: 'crítico', unavailable: 'indisponível' };
const brl0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const signed = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

type Ch = 'mercadolivre' | 'shopee' | 'ifood';

/** Dados do topo do canal (/general/channel), da loja escolhida quando o canal tem duas ou mais. */
export function useChannelBoard(channel: Ch) {
  // Canal com duas lojas ou mais: o painel é da loja escolhida na aba (a meta do mês é do canal e não aparece por loja).
  const accounts = useConnectedChannels().find((c) => c.code === channel)?.accounts.filter((a) => a.status !== 'revoked') ?? [];
  const account = accounts.length > 1 ? pickAccount(accounts, useSelectedAccountId(channel)) : null;
  const q = useQuery({ queryKey: ['general', 'channel', channel, account?.id ?? null],
    queryFn: () => api<ChannelData>(`/general/channel?channel=${channel}${account ? `&accountId=${account.id}` : ''}`), refetchInterval: 120_000 });
  return { q, account };
}

const healthLink = (channel: Ch) => (channel === 'shopee' ? '/canal/shopee/saude' : '/canais');

/** Alertas críticos de saúde (integração e conta). */
export function CriticalAlerts({ channel, d }: { channel: Ch; d: ChannelData }) {
  const healthTo = healthLink(channel);
  const critical = d.health.flatMap((h) => [...h.integration.map((x) => ({ ...x, to: '/canais' })), ...h.account.map((x) => ({ ...x, to: healthTo }))]
    .filter((x) => x.level === 'critical').map((x) => ({ ...x, account: h.name })));
  if (!critical.length) return null;
  return (
    <section aria-label="Alertas críticos do canal" className="alerts-grid">
      {critical.map((c, i) => (
        <div key={i} className="alert-card err"><span className="dot" aria-hidden />
          <div style={{ flex: 1 }}><b>{c.label}: {c.value ?? 'indisponível'}</b><div className="small muted">{c.account}{c.detail ? ` · ${c.detail}` : ''}</div></div>
          <Link className="small strong" to={c.to}>{c.to === '/canais' ? 'Ver a conexão' : 'Ver a saúde da conta'}</Link>
        </div>
      ))}
    </section>
  );
}

/** Cabeçalho de quadro com botão de recolher (quando onToggle é passado). */
function PanelTitle({ title, extra, collapsed, onToggle }: { title: string; extra?: ReactNode; collapsed?: boolean; onToggle?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <h2 style={{ margin: 0, flex: 1 }}>{title}{extra}</h2>
      {onToggle && (
        <button type="button" className="btn sm" onClick={onToggle} aria-expanded={!collapsed} title={collapsed ? 'Expandir' : 'Recolher'}>
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}<span className="sr-only">{collapsed ? 'Expandir' : 'Recolher'}</span>
        </button>
      )}
    </div>
  );
}

export function HealthPanel({ channel, d, collapsed, onToggle }: { channel: Ch; d: ChannelData; collapsed?: boolean; onToggle?: () => void }) {
  const healthTo = healthLink(channel);
  const worstLevel = d.health.reduce<Level>((a, h) => (h.level === 'critical' || (h.level === 'attention' && a === 'ok') ? h.level : a), 'ok');
  return (
    <section className="panel stack-y" style={{ gap: 10, alignSelf: 'start' }}>
      <PanelTitle title="Saúde da integração e da conta" collapsed={collapsed} onToggle={onToggle}
        extra={<>{channel === 'shopee' && <Link className="small" style={{ marginLeft: 8, fontWeight: 400 }} to={healthTo}>ver detalhes</Link>}
          {collapsed && d.health.length > 0 && <> <Tag tone={TONE[worstLevel]}>{LABEL[worstLevel]}</Tag></>}</>} />
      {!collapsed && (d.health.length === 0 ? <p className="small muted">Nenhuma conta conectada.</p> : d.health.map((h) => (
        <div key={h.accountId} className="health-list">
          <div className="small"><b>{h.name}</b> <Tag tone={TONE[h.level]}>{LABEL[h.level]}</Tag></div>
          {[...h.integration, ...h.account].map((x) => (
            <div key={x.metric} className="row-line">
              <span>{x.label}{x.detail && <span className="small muted"> · {x.detail.slice(0, 80)}</span>}</span>
              <span>{x.value ?? <span className="muted">indisponível</span>} <Tag tone={TONE[x.level]}>{LABEL[x.level]}</Tag></span>
            </div>
          ))}
          {h.account.length === 0 && <div className="small muted">Saúde da conta no canal: a primeira coleta sai em até 1 hora.</div>}
        </div>
      )))}
    </section>
  );
}

export function PendingPanel({ channel, d, collapsed, onToggle }: { channel: Ch; d: ChannelData; collapsed?: boolean; onToggle?: () => void }) {
  const pendingTo = channel === 'shopee' ? '/canal/shopee/publicar?situacao=pendente' : '/catalogo?status=unmatched';
  const total = d.catalog.pending + d.catalog.failed + d.pmc.above;
  return (
    <section className="panel stack-y" style={{ gap: 10, alignSelf: 'start' }}>
      <PanelTitle title="Pendências" collapsed={collapsed} onToggle={onToggle}
        extra={collapsed ? <span className="small muted" style={{ marginLeft: 8, fontWeight: 400 }}>{int(total)} no total</span> : undefined} />
      {!collapsed && (
        <>
          <div className="row-line"><span>Publicados</span><span className="tnum">{int(d.catalog.published)}</span></div>
          <div className="row-line"><span>Prontos para publicar</span><span className="tnum">{int(d.catalog.ready)}</span></div>
          <div className="row-line"><Link to={pendingTo}>Pendentes de cadastro</Link><span className="tnum">{int(d.catalog.pending)}</span></div>
          {d.catalog.reasons.map((r) => (
            <div key={r.reason} className="row-line small" style={{ paddingLeft: 12 }}>
              <Link to={channel === 'shopee' ? `/canal/shopee/publicar?situacao=pendente&motivo=${encodeURIComponent(r.code)}` : pendingTo}>{r.reason}</Link><span className="tnum">{int(r.n)}</span>
            </div>
          ))}
          {d.catalog.failed > 0 && (
            <div className="row-line"><Link to={channel === 'shopee' ? '/canal/shopee/publicacoes' : '/canal/mercadolivre/publicacoes?status=failed'}>Publicações recusadas (30 dias)</Link><span className="tnum">{int(d.catalog.failed)}</span></div>
          )}
          <div className="row-line"><Link to={`/canal/${channel}/precos`}>Ofertas acima do PMC oficial</Link>
            <span className="tnum" style={d.pmc.above ? { color: 'var(--err)', fontWeight: 600 } : undefined}>{int(d.pmc.above)}</span></div>
          <div className="row-line"><Link to={`/canal/${channel}/precos`}>A menos de 5% do PMC</Link><span className="tnum">{int(d.pmc.near)}</span></div>
          <p className="small muted" style={{ margin: 0 }}>PMC oficial da CMED/ABCFarma para a UF da loja. Acima do PMC: só observado nesta fase (nada é bloqueado).</p>
        </>
      )}
    </section>
  );
}

/** Painel do canal do iFood (e da visão por canal): projeção, mês, risco, saúde e pendências. */
export function ChannelBoard({ channel }: { channel: Ch }) {
  const { q, account } = useChannelBoard(channel);
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  if (!d) return <Loading />;
  const p = d.projection;
  return (
    <div className="stack-y" style={{ gap: 'var(--gap)' }}>
      <CriticalAlerts channel={channel} d={d} />
      <section className="kpis" aria-label="Resumo do dia no canal">
        <div className="kpi">
          <span className="k-label">Projeção do dia</span>
          <span className="k-value proj-number">{p.amount == null ? '—' : brl0(p.amount)}</span>
          {p.pacePct != null && <span className="small strong" style={{ display: 'block', color: p.pacePct >= 0 ? 'var(--ok)' : 'var(--err)' }}>{signed(p.pacePct)} vs. ritmo de referência</span>}
          <span className="k-note">{p.refDays.length ? `${refText(d.now.today, p.refDays)}, até as ${d.now.time}` : ''}
            {' · '}Média das últimas 4 semanas: dia útil {p.average.business == null ? 'sem venda' : brl0(p.average.business)} · fim de semana {p.average.weekend == null ? 'sem venda' : brl0(p.average.weekend)}</span>
        </div>
        <div className="kpi">
          <span className="k-label">Vendido hoje</span>
          <span className="k-value">{brl(p.soldToday)}</span>
          <span className="k-note">{int(p.ordersToday)} pedido{p.ordersToday === 1 ? '' : 's'}</span>
        </div>
        <div className="kpi">
          <span className="k-label">Mês</span>
          <span className="k-value">{brl0(d.month.amount)}</span>
          <div className="bar" aria-hidden><div style={{ width: `${Math.min(100, d.month.goalPct ?? 0)}%` }} /></div>
          <span className="k-note">{account ? `só ${account.displayName ?? 'esta loja'} · a meta é do canal (Geral)` : d.month.goal ? `${(d.month.goalPct ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}% da meta de ${brl0(d.month.goal)}${d.month.provisional ? ' (provisória)' : d.month.source === 'calculated' ? ' (sugerida)' : ''}` : <Link to="/configuracoes/metas">Definir a meta</Link>}</span>
        </div>
        <Link className={`kpi${d.risk.count ? ' alert' : ''}`} to={`/pedidos?alert=at_risk&channel=${channel}`}>
          <span className="k-label">Pedidos em risco de atraso</span>
          <span className="k-value">{channel === 'ifood' ? '—' : int(d.risk.count)}</span>
          <span className="k-note">{channel === 'ifood' ? 'O iFood não informa o prazo de despacho' : d.risk.late ? `${int(d.risk.late)} já passaram do prazo` : 'Prazo de envio em até 12 h, sem nota ou etiqueta'}</span>
        </Link>
      </section>

      <div className="cols">
        <HealthPanel channel={channel} d={d} />
        <PendingPanel channel={channel} d={d} />
      </div>
    </div>
  );
}
